import { useState, useEffect, useMemo } from "react";
import { Pencil, Trash2, RotateCcw, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, ChevronUp, ChevronDown, Search, X } from "lucide-react";
import { labelize } from "../utils/format";
import { SkeletonRows } from "./Loader";

export function DataTable({ rows, fields, onEdit, onDelete, onRestore, currencyFields = [], colorFields = {}, bgColorFields = {}, statusColors = {}, pageSizes = [5, 10, 25, 50], header, loading = false, emptyMessage, selectFields = {}, columnLabels = {} }) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(pageSizes.includes(10) ? 10 : (pageSizes[0] || 10));
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => { setPage(0); }, [rows.length, size]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((row) =>
      fields.some((f) => String(row[f] ?? "").toLowerCase().includes(term))
    );
  }, [rows, searchTerm, fields]);

  useEffect(() => { setPage(0); }, [searchTerm]);

  const totalPages = Math.ceil(filtered.length / size);

  function toggleSort(field) {
    if (sortKey === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(field);
      setSortDir("asc");
    }
  }

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { if (sortKey) setPage(0); }, [sortKey, sortDir]);

  const start = page * size;
  const visible = sorted.slice(start, start + size);

  return (
    <div>
      <div className="table-header">
        {header && <h3 className="table-title">{header}</h3>}
        {(rows.length > 0 || loading) && (
          <div className="pagination">
            <div className="search-input-wrap">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="search-clear" onClick={() => setSearchTerm("")}>
                  <X size={14} />
                </button>
              )}
              {searchTerm.trim() && (
                <span className="search-count">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
              )}
            </div>
            <span className="total-records">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
            <button className="secondary" disabled={loading || page === 0} onClick={() => setPage(0)}>
              <ChevronsLeft size={16} /> First
            </button>
            <button className="secondary" disabled={loading || page === 0} onClick={() => setPage(page - 1)}>
              <ChevronLeft size={16} /> Prev
            </button>
            <span className="page-info">Page {page + 1} of {totalPages || 1}</span>
            <button className="secondary" disabled={loading || page >= totalPages - 1} onClick={() => setPage(page + 1)}>
              Next <ChevronRight size={16} />
            </button>
            <button className="secondary" disabled={loading || page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>
              Last <ChevronsRight size={16} />
            </button>
            <select className="page-size-select" value={size} onChange={(e) => setSize(Number(e.target.value))}>
              {pageSizes.map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
                  {fields.map((field) => (
                    <th key={field} className="sortable" style={currencyFields.includes(field) ? { textAlign: "left" } : undefined} onClick={() => toggleSort(field)}>
                      <span>
                        {columnLabels[field] || labelize(field)}
                        {sortKey === field && (
                          sortDir === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </span>
                    </th>
                  ))}
              {(onEdit || onDelete) && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows columns={fields.length + ((onEdit || onDelete) ? 1 : 0)} rows={Math.min(size, 5)} />
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={fields.length + ((onEdit || onDelete) ? 1 : 0)}>
                  <div className="table-empty">{emptyMessage || "No records found."}</div>
                </td>
              </tr>
            ) : (
              visible.map((row) => (
              <tr key={row.id || row.uuid}>
                {fields.map((field) => {
                  const val = row[field] ?? "";
                  let display = String(val);
                  if (currencyFields.includes(field) && val !== "") {
                    display = `₹${Number(val).toLocaleString("en-IN")}`;
                  } else if (selectFields[field] && val !== "") {
                    const opt = selectFields[field].find((o) => String(o.value) === String(val) || o.value === val);
                    if (opt) display = opt.label || opt;
                  }
                  const statusColor = statusColors[field] ? (typeof statusColors[field] === "function" ? statusColors[field](val) : statusColors[field][String(val)]) : undefined;
                  const cellStyle = {
                    ...(colorFields[field] ? { color: colorFields[field] } : {}),
                    ...(statusColor ? { color: statusColor } : {}),
                  };
                  const hasBg = !!bgColorFields[field];
                  const content = hasBg
                    ? <span style={{ backgroundColor: bgColorFields[field], padding: "3px 8px", borderRadius: "4px" }}>{display}</span>
                    : display;
                  return <td key={field} style={Object.keys(cellStyle).length ? cellStyle : undefined}>{content}</td>;
                })}
{(onEdit || onDelete || onRestore) && (
                    <td className="actions">
                      {onEdit && <button title="Edit" aria-label="Edit" onClick={() => onEdit(row)}><Pencil size={15} /></button>}
                      {row?.isDeleted === true || row?.is_deleted === 1 || row?.is_deleted === true
                        ? (onRestore && <button title="Undo Delete" aria-label="Undo Delete" onClick={() => onRestore(row.uuid ?? row.id)}><RotateCcw size={15} /></button>)
                        : (onDelete && <button title="Delete" aria-label="Delete" onClick={() => onDelete(row.uuid ?? row.id)}><Trash2 size={15} /></button>)}
                    </td>
                  )}
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
